import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import NuevoEventoModal from '../components/NuevoEventoModal';

const ESTADOS = ['nuevo','en_atencion','resuelto','cerrado','falsa_alarma'];
const ESTADO_LABELS = { nuevo:'Nuevo', en_atencion:'En Atención', resuelto:'Resuelto', cerrado:'Cerrado', falsa_alarma:'Falsa Alarma' };
const PRIORIDADES = ['baja','media','alta','critica'];

export default function EventosPage() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [tiposEvento, setTiposEvento] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);

  const [filters, setFilters] = useState({
    estado: '', prioridad: '', cliente_id: '', monitorista_id: '',
    tipo_evento_id: '', vehiculo_id: '', fecha_inicio: '', fecha_fin: '', page: 1
  });

  const fetchEventos = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([,v]) => v !== ''));
      const res = await api.get('/eventos', { params });
      setEventos(res.data.data);
      setPagination(res.data.pagination);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchEventos(); }, [fetchEventos]);

  useEffect(() => {
    Promise.all([
      api.get('/catalogos/clientes?activo=true'),
      api.get('/catalogos/tipos-evento'),
      api.get('/auth/usuarios'),
      api.get('/catalogos/vehiculos?activo=true')
    ]).then(([c, te, u, v]) => {
      setClientes(c.data.data);
      setTiposEvento(te.data.data);
      setUsuarios(u.data.data);
      setVehiculos(v.data.data);
    }).catch(() => {});
  }, []);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val, page: 1 }));
  const clearFilters = () => setFilters({ estado:'', prioridad:'', cliente_id:'', monitorista_id:'', tipo_evento_id:'', vehiculo_id:'', fecha_inicio:'', fecha_fin:'', page:1 });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Eventos</div>
          <div className="page-subtitle">{pagination.total} eventos encontrados</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={14}/> Nuevo Evento
        </button>
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ paddingBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
            <select className="form-control" value={filters.estado} onChange={e => setFilter('estado', e.target.value)}>
              <option value="">Todos los estados</option>
              {ESTADOS.map(s => <option key={s} value={s}>{ESTADO_LABELS[s]}</option>)}
            </select>
            <select className="form-control" value={filters.prioridad} onChange={e => setFilter('prioridad', e.target.value)}>
              <option value="">Prioridad</option>
              {PRIORIDADES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
            </select>
            <select className="form-control" value={filters.cliente_id} onChange={e => setFilter('cliente_id', e.target.value)}>
              <option value="">Cliente</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
            <select className="form-control" value={filters.tipo_evento_id} onChange={e => setFilter('tipo_evento_id', e.target.value)}>
              <option value="">Tipo de evento</option>
              {tiposEvento.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
            <select className="form-control" value={filters.monitorista_id} onChange={e => setFilter('monitorista_id', e.target.value)}>
              <option value="">Monitorista</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>)}
            </select>
            <select className="form-control" value={filters.vehiculo_id} onChange={e => setFilter('vehiculo_id', e.target.value)}>
              <option value="">Vehículo</option>
              {vehiculos.map(v => <option key={v.id} value={v.id}>{v.numero_economico}</option>)}
            </select>
            <input className="form-control" type="date" value={filters.fecha_inicio} onChange={e => setFilter('fecha_inicio', e.target.value)} title="Fecha inicio"/>
            <input className="form-control" type="date" value={filters.fecha_fin} onChange={e => setFilter('fecha_fin', e.target.value)} title="Fecha fin"/>
          </div>
          <div style={{ marginTop: 10 }}>
            <button className="btn btn-secondary btn-sm" onClick={clearFilters}>Limpiar filtros</button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="table-wrap">
          {loading ? (
            <div className="loading-screen"><div className="spinner"/></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Fecha Alerta</th>
                  <th>Tipo</th>
                  <th>Prioridad</th>
                  <th>Vehículo</th>
                  <th>Cliente</th>
                  <th>Monitorista</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {eventos.length === 0 && <tr><td colSpan={8} className="table-empty">Sin resultados</td></tr>}
                {eventos.map(ev => (
                  <tr key={ev.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/eventos/${ev.id}`)}>
                    <td><span className="font-mono text-accent" style={{fontSize:11}}>{ev.folio}</span></td>
                    <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {ev.fecha_alerta ? format(new Date(ev.fecha_alerta), 'dd/MM/yy HH:mm', { locale: es }) : '—'}
                    </td>
                    <td style={{ maxWidth: 160, fontSize: 12 }}>
                      <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                        {ev.tipoEvento?.color && <span style={{ width:8, height:8, borderRadius:'50%', background:ev.tipoEvento.color, flexShrink:0 }}/>}
                        {ev.tipoEvento?.nombre || 'N/A'}
                      </span>
                    </td>
                    <td><span className={`badge badge-${ev.prioridad}`}>{ev.prioridad}</span></td>
                    <td><span className="tag">{ev.vehiculo?.numero_economico || 'N/A'}</span></td>
                    <td style={{ fontSize: 12 }}>{ev.cliente?.nombre || 'N/A'}</td>
                    <td style={{ fontSize: 12 }}>{ev.monitorista ? `${ev.monitorista.nombre} ${ev.monitorista.apellido}` : <span className="text-muted">Sin asignar</span>}</td>
                    <td><span className={`badge badge-${ev.estado}`}>{ESTADO_LABELS[ev.estado]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginación */}
        {pagination.pages > 1 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderTop:'1px solid var(--border)' }}>
            <span style={{ fontSize:12, color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>
              Pág. {pagination.page} de {pagination.pages} ({pagination.total} total)
            </span>
            <div style={{ display:'flex', gap:6 }}>
              <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1} onClick={() => setFilters(f => ({...f, page: f.page - 1}))}>
                <ChevronLeft size={14}/>
              </button>
              <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.pages} onClick={() => setFilters(f => ({...f, page: f.page + 1}))}>
                <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <NuevoEventoModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); fetchEventos(); }}
        />
      )}
    </div>
  );
}
